import {
  UPLOAD_ABORTED_ERROR_MSG,
  getNextUploadId,
  upload,
  type ServerStatus,
  type UploadFile
} from '@/services/media-upload.service'
import { useRef } from 'react'
import { toast } from 'sonner'

export default function Uploader({
  children,
  onUploadSuccess,
  onUploadStart,
  onUploadEnd,
  onUploadInit,
  onServerStatus,
  className,
  accept = 'image/*'
}: {
  children: React.ReactNode
  onUploadSuccess: ({ url }: { url: string }) => void
  onUploadStart?: (file: UploadFile, cancel: () => void) => void
  onUploadEnd?: (file: UploadFile) => void
  onUploadInit?: (info: { file: UploadFile; cancel: () => void; servers: string[] }) => void
  onServerStatus?: (file: UploadFile, server: string, status: ServerStatus) => void
  className?: string
  accept?: string
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const abortControllerMap = new Map<UploadFile, AbortController>()
    const files: UploadFile[] = Array.from(event.target.files).map((f) => {
      const file = f as UploadFile
      file._uploadId = getNextUploadId()
      return file
    })

    for (const file of files) {
      const abortController = new AbortController()
      abortControllerMap.set(file, abortController)
      onUploadStart?.(file, () => abortController.abort())
    }

    for (const file of files) {
      const abortController = abortControllerMap.get(file)
      try {
        const result = await upload(file, {
          signal: abortController?.signal,
          cancel: () => abortController?.abort(),
          onUploadInit,
          onServerStatus: (server, status) => onServerStatus?.(file, server, status)
        })
        onUploadSuccess(result)
        onUploadEnd?.(file)
      } catch (error) {
        console.error('Error uploading file', error)
        const message = (error as Error).message
        if (message !== UPLOAD_ABORTED_ERROR_MSG) {
          toast.error(`Failed to upload file: ${message}`)
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '' // clear the value so that the same file can be uploaded again
      fileInputRef.current.click()
    }
  }

  return (
    <div className={className}>
      <div onClick={handleUploadClick}>{children}</div>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        accept={accept}
        multiple
      />
    </div>
  )
}
